// TRACKING_PROPERTIES integration — owns `var.global.trackingProperties`, the
// attribution snapshot consumed by the pipeorders order POST (→ orders/api CAPI
// + NNP payloads), the payments-API session and the GTM/Klaviyo/Segment payment
// events.
//
// Flow: `setup` writes the landing anchor synchronously, then cookies are
// re-read on a bounded post-`load` ladder, on every page transition, on
// consent-granted, and on demand when a consumer emits
// `PIPE_EVENTS.trackingRefresh`. Everything for the integration lives in this
// file (rules, DOM/pipe wrappers, triggers).
//
// The snapshot has two halves with different lifetimes; keep them separate:
//
//   * The landing ANCHOR (pageUrl/pagePath/pageOrigin/queryParams/hashParams/
//     referrer/capturedAt) describes where the visit came from and is written
//     once. `queryParams.fbclid` is the only fbclid available anywhere, and
//     `pageOrigin` is read server-side by the GA4 conversions service, so
//     re-capturing the anchor on a later page silently loses both.
//   * COOKIES describe the browser now and must be re-read. `_fbp`/`_fbc` are
//     written by fbevents.js, which GTM injects asynchronously (GTM is
//     `headClientOnly`, so its snippet only lands at hydration): typically
//     0.5-3s after `load`, and not until the visitor accepts when Consent Mode
//     gating is on. A landing-time capture is too early to observe them.
//
// The same split holds in legacy (`pipelines/src/fbm-g/src/js/dataStore.js:162-202`):
// cookies re-read per page load, merged only when non-empty, origin query
// string set once. Legacy funnels were multi-page so page loads drove it; here
// routing is client-side, so the refresh has to be triggered explicitly.

import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type { PIPE_EVENTS, PipeInterface } from '@render/composables/usePipe'

/**
 * Channel this integration listens on for an on-demand refresh — canonically
 * `PIPE_EVENTS.trackingRefresh`, spelled out here rather than value-imported so
 * this module keeps only type-only imports and stays loadable by its bare-node
 * unit test (see the alias note in tsconfig.node.json). The annotation pins it
 * to the literal type of the real constant, so the two can never drift.
 */
const TRACKING_REFRESH_EVENT: (typeof PIPE_EVENTS)['trackingRefresh'] = 'tracking:refresh-cookies'

/**
 * The only pipe surface these helpers need. Structural, so `usePaymentData`
 * (which takes a narrowed pipe) can call them without widening to the full
 * interface — a real `PipeInterface` satisfies it.
 */
export type TrackingPipe = Pick<PipeInterface, 'getData' | 'setData'>

export const TRACKING_PROPERTIES_VAR_KEY = 'var.global.trackingProperties'

/**
 * Offsets from the ladder start (ms) for the post-load refresh attempts.
 * Bounded (no `setInterval`) and exits early once `isFbTargetSatisfied` — the
 * window only has to outlast GTM → container eval → fbevents.js → `fbq('init')`.
 */
export const REFRESH_DELAYS_MS = [0, 300, 800, 2_000, 5_000, 10_000] as const

export interface TrackingProperties {
  // ---- landing anchor: written once, never overwritten ----
  queryParams: Record<string, string>
  hashParams: Record<string, string>
  referrer?: string
  pageUrl: string
  pagePath: string
  pageOrigin: string
  /** Landing capture time (ms). Doubles as the `_fbc` creationTime, so it must stay stable. */
  capturedAt: number
  // ---- refreshed across the session ----
  cookies: Record<string, string>
  /** True while `cookies._fbc` is our fbclid-derived value rather than a real cookie. */
  fbcSynthesized?: boolean
}

/** The anchor half, as produced by a fresh landing capture. */
export type TrackingAnchor = Omit<TrackingProperties, 'cookies' | 'fbcSynthesized'>

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const asStringMap = (v: unknown): Record<string, string> => {
  if (!isPlainObject(v)) return {}
  const out: Record<string, string> = {}
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === 'string') out[k] = val
  }
  return out
}

/* -------------------------------------------------------------------------- */
/* Pure parsing                                                                */
/* -------------------------------------------------------------------------- */

/** Parse a `document.cookie` string. Malformed segments are skipped; never throws. */
export function parseCookieString(raw: string): Record<string, string> {
  const cookies: Record<string, string> = {}
  if (!raw) return cookies
  for (const segment of raw.split(';')) {
    const idx = segment.indexOf('=')
    if (idx < 0) continue
    const key = segment.slice(0, idx).trim()
    if (!key) continue
    const value = segment.slice(idx + 1).trim()
    try {
      cookies[key] = decodeURIComponent(value)
    } catch {
      // A stray `%` makes decodeURIComponent throw — keep the raw value rather
      // than dropping the whole cookie.
      cookies[key] = value
    }
  }
  return cookies
}

/** Parse a `?a=b&c=d` search string or a `#a=b` fragment. Never throws. */
export function parseParamString(raw: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (!raw) return params
  const body = raw.startsWith('?') || raw.startsWith('#') ? raw.slice(1) : raw
  if (!body || !body.includes('=')) return params
  try {
    new URLSearchParams(body).forEach((value, key) => {
      params[key] = value
    })
  } catch {
    // ignore — URLSearchParams unavailable in rare envs
  }
  return params
}

/* -------------------------------------------------------------------------- */
/* Cookie merge                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Per-key merge of a fresh `document.cookie` read over the stored map.
 *
 *  - a fresh non-empty value wins (a new fbclid legitimately rotates `_fbc`,
 *    `_ga` rolls its session segment);
 *  - a fresh EMPTY string never clobbers a stored value;
 *  - a stored key ABSENT from the fresh read is retained. `_fbp`/`_fbc` are
 *    ~90-day first-party cookies: mid-session disappearance means a blocked
 *    read, eviction or a consent tool deleting them — and the stored value is
 *    still exactly what the browser pixel already reported to Meta, so keeping
 *    it maximises match quality and fabricates nothing.
 */
export function mergeCookies(
  prev: Record<string, string>,
  fresh: Record<string, string>,
): { cookies: Record<string, string>; changed: boolean } {
  const cookies: Record<string, string> = { ...prev }
  let changed = false
  for (const [key, value] of Object.entries(fresh)) {
    if (!value) continue
    if (cookies[key] === value) continue
    cookies[key] = value
    changed = true
  }
  return { cookies, changed }
}

/* -------------------------------------------------------------------------- */
/* `_fbc` synthesis                                                            */
/* -------------------------------------------------------------------------- */

// Meta's click-id grammar, shared by `_fbp` and `_fbc`:
//   fb.<subdomainIndex>.<creationTimeMs>.<payload>
// `subdomainIndex` is the depth of the domain the cookie is defined on
// ('me' = 0, 'best.me' = 1, 'fasting.best.me' = 2). fbevents sets the cookie on
// the registrable domain (eTLD+1), so 1 is the near-universal value.
const FB_CLICK_ID_PREFIX = 'fb'

/** Cookie names, as constants so they can be used as computed keys in literals. */
export const FBP_COOKIE = '_fbp'
export const FBC_COOKIE = '_fbc'

// Second-level suffixes that make a 2-label tail a public suffix
// (`example.co.uk`). Deliberately a small list rather than a Public Suffix List
// dependency — see `deriveSubdomainIndex`.
const SECOND_LEVEL_SUFFIXES = new Set(['co', 'com', 'net', 'org', 'gov', 'edu', 'ac', 'or', 'ne'])

/** Accepts Meta's several fbclid encodings; rejects parse debris (spaces, `&`, `?`, quotes). */
const FBCLID_PATTERN = /^[A-Za-z0-9._~%+-]+$/

/**
 * Depth of the registrable domain, i.e. the `subdomainIndex` of a cookie the
 * pixel set on it. Prefers parsing it out of an existing `_fbp` — same grammar,
 * same pixel, same domain, so that value is exact rather than heuristic.
 *
 * The hostname fallback is a deliberate PSL-lite: it returns 1 for any normal
 * single-label TLD regardless of subdomain depth (correct, because the cookie
 * lands on eTLD+1 either way) and 2 for the common `*.co.uk` shape. An exotic
 * multi-label suffix can be wrong, which costs nothing structurally — Meta
 * still resolves the fbclid payload, and browser/server dedup keys on
 * `event_id`, not on fbc.
 */
export function deriveSubdomainIndex(hostname: string, fbpValue?: string): number {
  const fromFbp = Number(String(fbpValue ?? '').split('.')[1])
  if (Number.isInteger(fromFbp) && fromFbp >= 0 && fromFbp <= 10) return fromFbp

  const host = String(hostname ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
  if (!host || host === 'localhost') return 1
  // IPv4/IPv6 literals have no registrable domain.
  if (host.includes(':') || /^[\d.]+$/.test(host)) return 1

  const labels = host.split('.').filter(Boolean)
  if (labels.length <= 2) return 1

  const tld = labels[labels.length - 1] ?? ''
  const sld = labels[labels.length - 2] ?? ''
  if (tld.length === 2 && SECOND_LEVEL_SUFFIXES.has(sld)) return 2
  return 1
}

/** Assemble Meta's `_fbc` value. `creationTimeMs` is MILLISECONDS (unlike CAPI's eventTime). */
export function buildSyntheticFbc(
  fbclid: string,
  creationTimeMs: number,
  subdomainIndex: number,
): string {
  return `${FB_CLICK_ID_PREFIX}.${subdomainIndex}.${creationTimeMs}.${fbclid}`
}

/** Trim + sanity-check an fbclid candidate. Returns '' when unusable. */
export function normalizeFbclid(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const fbclid = raw.trim()
  if (!fbclid || fbclid === 'undefined' || fbclid === 'null') return ''
  if (fbclid.length > 512) return ''
  return FBCLID_PATTERN.test(fbclid) ? fbclid : ''
}

/**
 * Resolve the effective `_fbc`. A real cookie always wins; otherwise derive it
 * from the landing `fbclid` — Meta documents reconstructing fbc from the URL
 * parameter when the cookie is unreadable, and the payload is a genuine,
 * Meta-issued click id.
 *
 * `_fbp` is deliberately NOT synthesizable: it is a random id minted by
 * fbevents, so a value we invent would point at a different browser than the
 * one the pixel reported — splitting the user and LOWERING match quality.
 */
export function resolveFbc(
  cookies: Record<string, string>,
  queryParams: Record<string, string>,
  hostname: string,
  capturedAt: number,
): { fbc: string; synthesized: boolean } {
  const real = cookies[FBC_COOKIE]
  if (real) return { fbc: real, synthesized: false }

  const fbclid = normalizeFbclid(queryParams.fbclid)
  if (!fbclid || !Number.isFinite(capturedAt)) return { fbc: '', synthesized: false }

  const subdomainIndex = deriveSubdomainIndex(hostname, cookies[FBP_COOKIE])
  return { fbc: buildSyntheticFbc(fbclid, capturedAt, subdomainIndex), synthesized: true }
}

/* -------------------------------------------------------------------------- */
/* Snapshot refresh                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Merge a fresh cookie read into a stored snapshot, leaving the landing anchor
 * untouched, and (re)resolve `_fbc`.
 *
 * `changed` drives whether the caller writes at all: every committing
 * `pipe.setData` re-serializes the ENTIRE variable map into localStorage, so a
 * no-op refresh on each of ~40 page transitions would be pure write
 * amplification. Because the synthesized fbc is keyed on the stable
 * `capturedAt`, re-deriving it is byte-identical and reports no change.
 */
export function applyCookieRefresh(
  stored: TrackingProperties,
  fresh: Record<string, string>,
  hostname: string,
): { next: TrackingProperties; changed: boolean } {
  // A wholly empty read means the tags simply haven't written yet (or the read
  // was blocked) — never let it erase what we already have. Legacy parity:
  // dataStore.js:190 `if (cookiesData && Object.keys(cookiesData).length > 0)`.
  const merged = Object.keys(fresh).length
    ? mergeCookies(stored.cookies ?? {}, fresh)
    : { cookies: stored.cookies ?? {}, changed: false }

  // A synthetic `_fbc` we wrote earlier must not be mistaken for a real cookie
  // on the next pass (that would report `fbcSynthesized: false` and churn a
  // write). Hide it before re-resolving; an actual cookie in THIS read always
  // takes precedence and is kept.
  const resolveBase = { ...merged.cookies }
  if (stored.fbcSynthesized && !fresh[FBC_COOKIE]) delete resolveBase[FBC_COOKIE]

  const { fbc, synthesized } = resolveFbc(
    resolveBase,
    stored.queryParams ?? {},
    hostname,
    stored.capturedAt,
  )

  const cookies = fbc ? { ...resolveBase, [FBC_COOKIE]: fbc } : resolveBase
  const fbcSynthesized = fbc && synthesized ? true : undefined

  const changed =
    merged.changed ||
    cookies[FBC_COOKIE] !== (stored.cookies ?? {})[FBC_COOKIE] ||
    fbcSynthesized !== stored.fbcSynthesized

  return { next: { ...stored, cookies, fbcSynthesized }, changed }
}

/**
 * True once there is nothing further the refresh ladder could pick up: `_fbp`
 * is present and `_fbc` is either present or underivable (no fbclid landed).
 */
export function isFbTargetSatisfied(tp: TrackingProperties | undefined): boolean {
  if (!tp) return false
  const cookies = tp.cookies ?? {}
  if (!cookies[FBP_COOKIE]) return false
  if (cookies[FBC_COOKIE]) return true
  return !normalizeFbclid((tp.queryParams ?? {}).fbclid)
}

/* -------------------------------------------------------------------------- */
/* DOM + pipe wrappers                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Read every readable cookie. Returns `null` when the read THREW (sandboxed
 * iframe / blocked storage) — distinct from `{}` ("nothing set yet"), because
 * only the latter is a real observation.
 */
export function readDocumentCookies(): Record<string, string> | null {
  if (typeof document === 'undefined') return null
  try {
    return parseCookieString(document.cookie)
  } catch {
    return null
  }
}

/** Snapshot the current URL as the landing anchor. Browser-only. */
export function captureLandingAnchor(now: number = Date.now()): TrackingAnchor {
  return {
    queryParams: parseParamString(window.location.search),
    hashParams: parseParamString(window.location.hash),
    referrer: document.referrer || undefined,
    pageUrl: window.location.href,
    pagePath: window.location.pathname,
    pageOrigin: window.location.origin,
    capturedAt: now,
  }
}

/** Read the stored snapshot, normalizing the string maps. */
export function readTrackingProperties(pipe: TrackingPipe): TrackingProperties | undefined {
  const raw = pipe.getData(TRACKING_PROPERTIES_VAR_KEY)
  if (!isPlainObject(raw)) return undefined
  return {
    ...(raw as unknown as TrackingProperties),
    queryParams: asStringMap(raw.queryParams),
    hashParams: asStringMap(raw.hashParams),
    cookies: asStringMap(raw.cookies),
  }
}

const currentHostname = (): string =>
  typeof window === 'undefined' ? '' : (window.location?.hostname ?? '')

/**
 * Refresh only the cookie half of the stored snapshot. Returns true when a
 * write actually happened.
 *
 * Returns false without writing when no snapshot exists yet, so a refresh
 * arriving before the landing capture (an early `trackingRefresh` emit) can
 * never invent a half-built variable — the anchor is only ever established by
 * `captureTrackingProperties`.
 */
export function refreshTrackingCookies(pipe: TrackingPipe): boolean {
  if (typeof window === 'undefined') return false

  const stored = readTrackingProperties(pipe)
  if (!stored) return false

  const fresh = readDocumentCookies()
  if (fresh === null) return false

  const { next, changed } = applyCookieRefresh(stored, fresh, currentHostname())
  if (!changed) return false

  // Always write the WHOLE object. `setData` is a flat-map write
  // (`variables.value[key] = value`), so a dotted key like
  // `…trackingProperties.cookies` creates a sibling variable that shadows reads
  // while the serialized snapshot stays stale.
  pipe.setData(TRACKING_PROPERTIES_VAR_KEY, next)
  return true
}

/**
 * Establish (or repair) the snapshot.
 *
 *  - `reset: true`, or no usable stored snapshot → capture a fresh anchor.
 *  - otherwise → fill in anchor fields that are absent (a snapshot persisted by
 *    a build without `capturedAt`) and refresh cookies, but never re-anchor.
 *    Callers include paths that run on a mid-funnel page, where re-anchoring
 *    would replace the landing URL and drop `queryParams.fbclid` for the rest
 *    of the session.
 */
export function captureTrackingProperties(pipe: TrackingPipe, opts?: { reset?: boolean }): void {
  if (typeof window === 'undefined') return

  const stored = opts?.reset ? undefined : readTrackingProperties(pipe)
  const cookies = readDocumentCookies() ?? {}

  if (!stored || !stored.pageUrl) {
    const anchor = captureLandingAnchor()
    const { fbc, synthesized } = resolveFbc(
      cookies,
      anchor.queryParams,
      currentHostname(),
      anchor.capturedAt,
    )
    pipe.setData(TRACKING_PROPERTIES_VAR_KEY, {
      ...anchor,
      cookies: fbc ? { ...cookies, [FBC_COOKIE]: fbc } : cookies,
      fbcSynthesized: fbc ? synthesized : undefined,
    } satisfies TrackingProperties)
    return
  }

  const repaired: TrackingProperties = {
    ...stored,
    capturedAt: Number.isFinite(stored.capturedAt) ? stored.capturedAt : Date.now(),
  }
  const { next, changed } = applyCookieRefresh(repaired, cookies, currentHostname())
  if (changed || repaired.capturedAt !== stored.capturedAt) {
    pipe.setData(TRACKING_PROPERTIES_VAR_KEY, next)
  }
}

/* -------------------------------------------------------------------------- */
/* Integration: when to look                                                   */
/* -------------------------------------------------------------------------- */

const trackingPropertiesIntegration: PipeIntegration = (): PipeIntegrationDef => {
  let ladderTimer: ReturnType<typeof setTimeout> | undefined
  let ladderStep = 0

  /**
   * Bounded post-load retry ladder, sized for the chain that has to complete
   * before `_fbp` exists: hydration → gtm.js → container eval → Meta tag (often
   * on a *Window Loaded* trigger) → fbevents.js → `fbq('init')`, i.e. 0.5-3s
   * after `load`. Not gated on `window.fbq` — the container may load the pixel
   * from a custom HTML tag under any name.
   */
  const startLadder = (pipe: PipeInterface) => {
    if (ladderTimer) clearTimeout(ladderTimer)
    ladderStep = 0

    const attempt = () => {
      ladderTimer = undefined
      refreshTrackingCookies(pipe)
      if (isFbTargetSatisfied(readTrackingProperties(pipe))) return
      schedule()
    }

    // REFRESH_DELAYS_MS are offsets from the ladder start, so each timer waits
    // only the gap to the next one — the whole ladder spans the last offset.
    const schedule = () => {
      if (ladderStep >= REFRESH_DELAYS_MS.length) return
      const previous = ladderStep === 0 ? 0 : (REFRESH_DELAYS_MS[ladderStep - 1] ?? 0)
      const gap = Math.max(0, (REFRESH_DELAYS_MS[ladderStep] ?? 0) - previous)
      ladderStep += 1
      ladderTimer = setTimeout(attempt, gap)
    }

    schedule()
  }

  return {
    setup(pipe: PipeInterface) {
      if (typeof window === 'undefined') return

      // The anchor must exist synchronously: gtm.setup's legacy
      // `${distributionPath}:data` mirror reads it during its own setup.
      captureTrackingProperties(pipe)

      // `load` may already have fired by the time setup runs (a late-registered
      // extension, or hydration finishing after load), and a listener attached
      // then never fires — so branch on readyState rather than only listening.
      if (document.readyState === 'complete') {
        startLadder(pipe)
      } else {
        window.addEventListener('load', () => startLadder(pipe), { once: true })
      }

      // Per-page refresh, standing in for the page loads that drove the legacy
      // per-page capture. `whenReady()` is required: the first `navigation:show`
      // fires from onMounted, ahead of loadPersistedData's setTimeout(0) merge,
      // and that merge overwrites anything written before it.
      pipe.subscribe('navigation:show', async () => {
        await pipe.whenReady()
        refreshTrackingCookies(pipe)
      })

      // With Consent Mode gating, no analytics cookie exists until Accept —
      // which can land minutes after load, long past the ladder.
      pipe.subscribe('cookie-consent:changed', (decision: 'granted' | 'denied') => {
        if (decision === 'granted') startLadder(pipe)
      })

      // On-demand refresh for anything about to serialize the snapshot outbound
      // (pipeorders' order POST, the payments session, the payment:* payloads).
      // Consumers emit instead of importing so they stay correct on pipes where
      // this integration is disabled. `subscribe` runs the callback inline, so a
      // consumer can read the refreshed snapshot on the line after its emit —
      // keep this handler synchronous or that contract breaks.
      pipe.subscribe(TRACKING_REFRESH_EVENT, () => refreshTrackingCookies(pipe))

      // Re-anchor after `clear_all_pipe_data` / landing re-entry / DevTools
      // reset. MUST stay synchronous: gtm.ts defers its mirror rewrite by one
      // macrotask on this same channel, expecting us to have flushed first.
      // A full reset (not a merge) is right here — DevTools expires every
      // cookie before emitting, and clearPersistedData wipes the variable.
      pipe.subscribe('pipe:data-cleared', () => {
        captureTrackingProperties(pipe, { reset: true })
        startLadder(pipe)
      })
    },

    onDataLoaded(pipe: PipeInterface) {
      // Runs after loadPersistedData's merge, which lets a persisted snapshot
      // win over setup()'s capture. Repair missing anchor fields and refresh
      // cookies — but do NOT re-capture the anchor: this hook also fires on a
      // reload of a MID-FUNNEL page, where a full re-capture would replace the
      // landing URL and wipe `queryParams.fbclid` for the rest of the session.
      captureTrackingProperties(pipe)
    },
  }
}

export default trackingPropertiesIntegration
